// MFA gating helpers — the single source of truth for "which roles must have MFA"
// and "does this signed-in user still need to enrol".
//
// Used by BOTH useAuthSession.login AND the session-restore path in portal.jsx, so
// MFA enrolment is enforced on EVERY door into the app — explicit email+password
// login, an email/recovery link, and a page refresh — not just explicit login.
// (Before this, enrolment was only checked in login(), so a brand-new staff user
// who arrived via their invite/reset link landed in the app with no MFA until they
// next logged in the normal way — the 15 June MFA-gate fix.)

import { ROLES } from "./enums";
import type { TypedSupabaseClient } from "./supabase";

// All internal Zephyr staff roles must enrol in MFA (shelter users are exempt).
// `combined` is a staff-side role too; `superuser` is the top internal tier.
// Don't re-list these elsewhere — import this.
export function isMfaGatedRole(role: string | null | undefined): boolean {
  return (
    role === ROLES.STAFF ||
    role === ROLES.ADMIN ||
    role === ROLES.SUPERUSER ||
    role === ROLES.PICKER ||
    role === ROLES.COMBINED
  );
}

// True when the signed-in user has NO verified TOTP factor and must enrol before
// using the app. Returns false (don't block) if the factor lookup errors — mirrors
// login()'s long-standing "don't hard-block on an MFA-check error" behaviour.
export async function needsMfaEnrollment(client: TypedSupabaseClient): Promise<boolean> {
  try {
    const { data } = await client.auth.mfa.listFactors();
    const hasVerified = !!data?.totp?.some((f) => f.status === "verified");
    return !hasVerified;
  } catch {
    return false;
  }
}
